import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.append("username", email);
      params.append("password", password);
      const res = await api.post("/api/auth/login", params);
      localStorage.setItem("token", res.data.access_token);
      localStorage.setItem("role", res.data.role);
      localStorage.setItem("name", res.data.name);
      localStorage.setItem("email", email);

      try {
        const me = await api.get("/api/auth/me", {
          headers: { Authorization: `Bearer ${res.data.access_token}` },
        });
        localStorage.setItem("project_roles", JSON.stringify(me.data.project_roles || []));
        localStorage.setItem("user_id", me.data.id || "");
      } catch {
        localStorage.setItem("project_roles", "[]");
      }

      // Clear upload state from any previous session
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith("upload_"))
        .forEach((k) => sessionStorage.removeItem(k));

      navigate("/select-project");
    } catch (e: any) {
      if (e.response?.status === 403) {
        setError(e.response?.data?.detail || "Your access has not been confirmed by an admin yet");
      } else {
        setError("Invalid email or password");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    /* Gray background so the card margin shows — matches Figma */
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6">

      {/* Card with rounded corners and shadow */}
      <div
        className="flex w-full rounded-2xl overflow-hidden shadow-2xl"
        style={{ minHeight: "calc(100vh - 48px)" }}
      >

        {/* ── Left Panel ── */}
        <div
          className="relative flex flex-col overflow-hidden"
          style={{ width: "62%" }}
        >
          {/* Solid blue background */}
          <div
            className="absolute inset-0"
            style={{ backgroundColor: "#1d4ed8" }}
          />

          {/* Plane PNG — flipped horizontally so nose points right-to-left (matches Figma)
              mixBlendMode removed so the plane is clearly visible against the blue bg */}
          <img
            src="/plane.png"
            alt="Aircraft"
            className="absolute"
            style={{
            bottom: "-5%",
            left: "50%",
            transform: "translateX(-50%) scaleX(-1)",  // centered + flipped
            width: "75%",
            objectFit: "contain",
            opacity: 0.92,
          }}
          />

          {/* Content on top */}
          <div className="relative z-10 flex flex-col h-full p-10">

            {/* Logo — top-left */}
            <div className="flex items-center gap-2.5">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center border border-white border-opacity-40"
                style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <p className="text-white text-xs font-medium tracking-wide" style={{ opacity: 0.8 }}>
                  Collins Aerospace
                </p>
                <p className="text-white font-semibold text-sm leading-tight">EAC Reporting</p>
              </div>
            </div>

            {/* Headline — vertically centered */}
            <div className="flex-1 flex flex-col justify-center">
              <h2 className="text-white font-bold text-4xl leading-tight mb-4">
                Earned Value<br />AI-Powered<br />Reporting
              </h2>
              <p
                className="text-sm leading-relaxed max-w-xs"
                style={{ color: "rgba(219,234,254,0.9)" }}
              >
                
              </p>
            </div>

          </div>
        </div>

        {/* ── Right Panel ── */}
        <div
          className="flex items-center justify-center bg-white"
          style={{ width: "38%" }}
        >
          <div className="w-full max-w-md px-10">
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Welcome back</h1>
              <p className="text-sm text-gray-500">
                Sign in to your Collins Aerospace EAC Reporting account
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">

              {/* Email */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">Email address</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="sarah.chen@collinsaero.com"
                    required
                    className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">Password</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="w-full border border-gray-200 rounded-lg pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {/* Forgot Password — below input, right-aligned */}
                <div className="flex justify-end mt-1.5">
                  <button type="button" className="text-xs text-blue-600 hover:underline">
                    Forgot Password?
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="remember" className="text-sm text-gray-600">
                  Remember me for 30 days
                </label>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* Sign in */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">or continue with</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* SSO */}
              <button
                type="button"
                className="w-full border border-gray-200 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                RTX Single Sign-On (SSO)
              </button>
              <p className="text-xs text-gray-400 text-center">Don't have an account? <button type="button" onClick={() => navigate("/signup")} className="text-blue-600 hover:underline font-medium">Create account</button></p>

            </form>
          </div>
        </div>

      </div>
    </div>
  );
}
